import { School, SchoolPublicSite, SchoolPublicSiteAppearance, SchoolPublicSitePages, SchoolPublicSitePrivacy } from 'models/school';
import { KIND_SERVER_TO_CLIENT_MAPPING } from 'consts/school';

export const DEFAULT_SCHOOL_PUBLIC_SITE_PAGES: SchoolPublicSitePages = {
   home: true,
   calendar: true,
   fixturesAndResults: true,
   teams: false,
   records: false,
   photos: false,
   collections: true,
   tournaments: true,
   leagues: true,
   challenges: true,
   clubEvents: true,
   news: true,
   housePoints: true,
   search: false,
   tournamentResults: true,
   memberSchools: true,
   venues: true,
   resources: true,
};

export const DEFAULT_SCHOOL_PUBLIC_SITE_APPEARANCE: SchoolPublicSiteAppearance = {
   brandColor: '#164170',
   headerImageUrl: '',
};

export const DEFAULT_SCHOOL_PUBLIC_SITE_PRIVACY: SchoolPublicSitePrivacy = {
   studentNames: 'FIRST_NAME_AND_INITIAL',
   photos: 'PUBLIC_ALBUMS_ONLY',
   staff: 'NAME_AND_ROLE',
   individualPerformance: 'NAMED_PERFORMANCES',
};

export interface ResolvedSchoolPublicSite extends SchoolPublicSite {
   pages: SchoolPublicSitePages;
   appearance: SchoolPublicSiteAppearance;
   privacy: SchoolPublicSitePrivacy;
   isSearchEngineIndexingEnabled: boolean;
}

const HEX_COLOR = /^#[0-9a-f]{6}$/i;

export function resolveSchoolPublicSite(school: School): ResolvedSchoolPublicSite {
   const publicSite = school.publicSite || ({} as SchoolPublicSite);
   const pages = publicSite.pages || {};
   const legacyCollections = typeof pages.collections === 'boolean' ? pages.collections : true;
   const unionDefaults = school.kind === KIND_SERVER_TO_CLIENT_MAPPING.SCHOOL_UNION ? {
      records: true, photos: true, tournamentResults: true, memberSchools: true, venues: true, resources: true
   } : {};

   return {
      ...publicSite,
      pages: {
         ...DEFAULT_SCHOOL_PUBLIC_SITE_PAGES,
         ...unionDefaults,
         ...pages,
         home: true,
         tournaments: typeof pages.tournaments === 'boolean' ? pages.tournaments : legacyCollections,
         leagues: typeof pages.leagues === 'boolean' ? pages.leagues : legacyCollections,
         challenges: typeof pages.challenges === 'boolean' ? pages.challenges : legacyCollections,
      },
      appearance: {
         ...DEFAULT_SCHOOL_PUBLIC_SITE_APPEARANCE,
         ...(publicSite.appearance || {}),
      },
      privacy: {
         ...DEFAULT_SCHOOL_PUBLIC_SITE_PRIVACY,
         ...(publicSite.privacy || {}),
      },
      isSearchEngineIndexingEnabled: publicSite.isSearchEngineIndexingEnabled !== false,
   };
}

function hexToRgb(color: string) {
   const safeColor = HEX_COLOR.test(color) ? color : DEFAULT_SCHOOL_PUBLIC_SITE_APPEARANCE.brandColor;
   const value = Number.parseInt(safeColor.slice(1), 16);

   return {
      red: (value >> 16) & 255,
      green: (value >> 8) & 255,
      blue: value & 255,
   };
}

function rgbToHex(red: number, green: number, blue: number) {
   const channel = (value: number) => Math.round(value).toString(16).padStart(2, '0');
   return `#${channel(red)}${channel(green)}${channel(blue)}`;
}

function relativeLuminance(red: number, green: number, blue: number) {
   const convert = (value: number) => {
      const channel = value / 255;
      return channel <= 0.03928 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;
   };

   return 0.2126 * convert(red) + 0.7152 * convert(green) + 0.0722 * convert(blue);
}

function contrastWithWhite(red: number, green: number, blue: number) {
   return 1.05 / (relativeLuminance(red, green, blue) + 0.05);
}

export function getAccessibleBrandColor(color: string) {
   const original = hexToRgb(color);
   let candidate = original;

   for (let mix = 0; contrastWithWhite(candidate.red, candidate.green, candidate.blue) < 4.5 && mix <= 0.75; mix += 0.05) {
      candidate = {
         red: original.red * (1 - mix),
         green: original.green * (1 - mix),
         blue: original.blue * (1 - mix),
      };
   }

   return rgbToHex(candidate.red, candidate.green, candidate.blue);
}

export function mixBrandColor(color: string, target: '#ffffff' | '#000000', amount: number) {
   const source = hexToRgb(color);
   const targetValue = target === '#ffffff' ? 255 : 0;
   const ratio = Math.max(0, Math.min(1, amount));

   return rgbToHex(
      source.red + (targetValue - source.red) * ratio,
      source.green + (targetValue - source.green) * ratio,
      source.blue + (targetValue - source.blue) * ratio,
   );
}
